/* Infection & conversion (feature:convert), client side.
   - Setup panel: "Infektion" / "Bekehrung" toggles; adds the options and the zombies' extra reserve to the battle setup.
   - Recolouring: a converted unit stays in its army's instanced mesh (same blueprint). Each unit geometry gets the
     vertex colours of the other team (aColB, from a second buildArmy for the other team) and an instance attribute
     aCv = [swap 0..1, glow] (glow > 0 green for the risen, < 0 golden for the converted).
   - Priest beams + light pillars, rise and conversion particles, HUD counter "+N infiziert / bekehrt".
   Simulation and rules: src/sim/features/convert.js. */
import * as THREE from 'three';
import { buildArmy } from '../game/voxel.js';
import { convertRules, KIND_RISE, KIND_CONVERT, RISE_DELAY } from '../sim/features/convert.js';
import './convert.css';

const nf = new Intl.NumberFormat('de-DE');
const opt = { inf: true, conv: true };
const MAXB = 256;
let deps = null, rules = null, armies = null;
let N = 0, split = 0, groups = null; // groups: [team, start, end] per unit group (from the worker)
let cvs = new Float32Array(0);   // per unit: swap, glow
let base = new Float32Array(0);  // per unit: lasting green shimmer (zombie chickens)
let seen = new Uint32Array(0), seenN = 0; // one pillar per target and frame
const act = [];                  // running transitions { i, k: 1 rise / 2 conversion, t0, swap }
let beams = null, nb = 0, cnt = [0, 0, 0, 0];
let ui = null, hudEl = null, beamMesh = null, pillarMesh = null;
const _m = new THREE.Matrix4(), _q = new THREE.Quaternion(), _p = new THREE.Vector3(), _s = new THREE.Vector3(), _d = new THREE.Vector3(), _c = new THREE.Color();
const ZA = new THREE.Vector3(0, 0, 1);

/** deps = { scene, fx: () => fx, debris: () => debris, resync: () => void } */
export function init(d) {
  deps = d;
  buildUI(); buildHUD();
  const mat = () => new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.6, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide, fog: false });
  beamMesh = new THREE.InstancedMesh(new THREE.BoxGeometry(1, 1, 1), mat(), MAXB);
  pillarMesh = new THREE.InstancedMesh(new THREE.CylinderGeometry(1, 0.7, 1, 16, 1, true).translate(0, 0.5, 0), mat(), MAXB);
  for (const m of [beamMesh, pillarMesh]) { m.setColorAt(0, _c.setRGB(0, 0, 0)); m.count = 0; m.frustumCulled = false; m.renderOrder = 2; deps.scene.add(m); }
  syncUI();
}

function buildUI() {
  const start = document.querySelector('#start'); if (!start) return;
  const box = document.createElement('div'); box.className = 'convert-box';
  box.innerHTML = `<div class="h">Infektion &amp; Bekehrung</div>
    <div class="row genrow"><button class="ghost" id="convertInf" aria-pressed="true">Infektion: an</button><button class="ghost" id="convertConv" aria-pressed="true">Bekehrung: an</button></div>
    <p class="sdesc" id="convertDesc"></p>`;
  start.before(box);
  ui = { inf: box.querySelector('#convertInf'), conv: box.querySelector('#convertConv'), desc: box.querySelector('#convertDesc') };
  ui.inf.addEventListener('click', () => { opt.inf = !opt.inf; syncUI(); deps.resync(); });
  ui.conv.addEventListener('click', () => { opt.conv = !opt.conv; syncUI(); deps.resync(); });
}
/** set both options directly (campaign missions: the level decides, even while a switch is greyed out) */
export function setOptions(inf, conv) { opt.inf = !!inf; opt.conv = !!conv; syncUI(); }
function syncUI() {
  if (!ui) return;
  const has = rules ? rules.has : { zombie: false, priest: false };
  const set = (b, on, ok, name, why) => { b.textContent = `${name}: ${on ? 'an' : 'aus'}`; b.setAttribute('aria-pressed', String(on)); b.disabled = !ok; b.title = ok ? '' : why; };
  set(ui.inf, opt.inf, has.zombie, 'Infektion', 'Nur wenn eine Seite Zombies hat');
  set(ui.conv, opt.conv, has.priest, 'Bekehrung', 'Nur wenn eine Seite Hohepriester hat');
  const d = [];
  if (has.zombie) d.push(`Wer von Zombies getötet wird, steht nach ${String(RISE_DELAY).replace('.', ',')} s als Zombie wieder auf (Hühner als Zombie-Hühner). Riesen und Maschinen bleiben liegen.`);
  if (has.priest) d.push('Hohepriester bekehren Feinde, statt zu schlagen. Mutige widerstehen länger, Furchtlose und Riesen gar nicht.');
  ui.desc.textContent = d.length ? d.join(' ') : 'Wirkt, sobald eine Seite Zombies oder Hohepriester hat.';
}
function buildHUD() {
  hudEl = [0, 1].map(t => {
    const side = document.querySelector(t ? '.score .side.r' : '.score .side.b'); if (!side) return null;
    const s = document.createElement('span'); s.className = 'convert-n convert-n' + t; s.hidden = true; side.appendChild(s); return s;
  });
}
function hud() {
  if (!hudEl) return;
  for (let t = 0; t < 2; t++) {
    const el = hudEl[t]; if (!el) continue;
    const p = [];
    if (cnt[t]) p.push(`+${nf.format(cnt[t])} infiziert`);
    if (cnt[2 + t]) p.push(`+${nf.format(cnt[2 + t])} bekehrt`);
    el.textContent = p.join(' / '); el.hidden = !p.length;
  }
}

/** bldSetup() hook: battle setup -> worker options + extra reserve for the zombies. Returns bm. */
export function setup(bm, arms, paint) {
  act.length = 0; beams = null; nb = 0; cnt = [0, 0, 0, 0]; cvs.fill(0); base.fill(0); hud();
  if (beamMesh) { beamMesh.count = 0; pillarMesh.count = 0; }
  if (!arms || !arms.some(A => A && A.team === 0) || !arms.some(A => A && A.team === 1)) return bm;
  armies = arms;
  // per unit group (mixed armies); counts are set before this hook (setupBattle: painted units per group)
  const info = arms.map(A => ({ key: A.cfg.type, name: A.ty.name, recipe: A.ty.recipe || '', stats: A.stats, count: A.stats.count, team: A.team }));
  rules = convertRules(info, bm.reserve, opt);
  for (let t = 0; t < 2; t++) bm.reserve[t] += rules.reserveAdd[t];
  for (const A of arms) if (A.lod0) apply(A);
  syncUI();
  bm.convert = rules.sim;
  return bm;
}

/** setupExtras hook (main.js): the options as the worker gets them, for multiplayer clients */
export const extras = () => ({ convert: rules ? rules.sim : null });
/** take over options decided elsewhere (multiplayer host, a replay's setup): only the look (recolouring, glow) */
export function adopt(sim, arms) {
  act.length = 0; beams = null; nb = 0; cnt = [0, 0, 0, 0]; cvs.fill(0); base.fill(0); hud();
  armies = arms;
  const ok = sim && Array.isArray(sim.rise) && Array.isArray(sim.chan);
  rules = {
    has: rules ? rules.has : { zombie: false, priest: false }, sim: ok ? sim : null, reserveAdd: [0, 0],
    alt: arms.map((A, g) => !!ok && (sim.chan[g] > 0 || sim.rise[g] === 2)),
    glow: arms.map((A, g) => !!ok && (sim.chan[g] > 0 || sim.rise[g] === 2 || (!!(sim.zombie || [])[g] && arms.some((B, h) => B && A && B.team !== A.team && sim.rise[h] === 1)))),
  };
  for (const A of arms) if (A && A.lod0) apply(A);
}

/** unitMeshes() hook: every unit geometry gets aColB + aCv, so the shared shader always finds them. */
export function meshes(A) {
  for (const im of [A.lod0, A.lod1]) {
    const g = im.geometry, a = new THREE.InstancedBufferAttribute(new Float32Array(im.instanceMatrix.count * 2), 2);
    a.setUsage(THREE.DynamicDrawUsage); g.setAttribute('aCv', a);
    g.setAttribute('aColB', g.getAttribute('color'));
  }
  A.cv = null;
  apply(A);
}
function apply(A) {
  const t = armies ? Math.max(0, armies.indexOf(A)) : A.team, r = rules; // rules are per unit group
  A.cv = r && (r.alt[t] || r.glow[t]) ? [A.lod0.geometry.getAttribute('aCv'), A.lod1.geometry.getAttribute('aCv')] : null;
  const alt = r && r.alt[t] ? altColors(A) : null;
  [A.lod0, A.lod1].forEach((im, k) => {
    const g = im.geometry, own = g.getAttribute('color'), b = alt && alt[k] && alt[k].count === own.count ? alt[k] : own;
    if (g.getAttribute('aColB') !== b) g.setAttribute('aColB', b);
  });
}
// the same blueprint meshed with the other team's colours (identical vertex order), built once per army
function altColors(A) {
  if (!A.cvAlt) {
    try { const B = buildArmy(1 - A.team, A.cfg); A.cvAlt = [B.geo0.getAttribute('color'), B.geo1.getAttribute('color')]; } catch (e) { A.cvAlt = []; }
  }
  return A.cvAlt;
}

/** makeUnitMaterial() hook: mix in the other team's colours and add the glow. */
export function shader(sh) {
  sh.vertexShader = sh.vertexShader.replace('void main() {', 'attribute vec3 aColB; attribute vec2 aCv; varying float vCvG;\nvoid main() {')
    .replace('#include <color_vertex>', '#include <color_vertex>\nvColor.rgb = mix(vColor.rgb, aColB, aCv.x); vCvG = aCv.y;');
  sh.fragmentShader = sh.fragmentShader.replace('void main() {', 'varying float vCvG;\nvoid main() {')
    .replace('#include <emissivemap_fragment>', '#include <emissivemap_fragment>\ntotalEmissiveRadiance += (vCvG > 0.0 ? vec3(0.32, 1.0, 0.22) : vec3(1.0, 0.78, 0.3)) * abs(vCvG) * 0.6;');
}

/** renderUnits() hooks: per instance k of unit i (near = close-up LOD), then the upload. */
export function instance(A, near, k, i) {
  const a = A.cv[near ? 0 : 1].array, c = k * 2, o = i * 2;
  if (o < cvs.length) { a[c] = cvs[o]; a[c + 1] = cvs[o + 1]; } else { a[c] = 0; a[c + 1] = 0; }
}
export function flush(A, n0, n1) {
  const a0 = A.cv[0], a1 = A.cv[1];
  if (n0) { a0.clearUpdateRanges(); a0.addUpdateRange(0, n0 * 2); a0.needsUpdate = true; }
  if (n1) { a1.clearUpdateRanges(); a1.addUpdateRange(0, n1 * 2); a1.needsUpdate = true; }
}

/** worker 'convert' message: team changes, effects, beams, counters. */
export function frame(m) {
  split = m.split; groups = Array.isArray(m.groups) ? m.groups : null;
  if (m.N !== N) { N = m.N; cvs = new Float32Array(N * 2); base = new Float32Array(N); seen = new Uint32Array(N); act.length = 0; }
  if (m.fx && m.fx.length) effects(m.fx);
  if (m.team) applyTeam(m.team);
  beams = m.beams; nb = beams ? beams.length / 3 : 0;
  cnt = m.n; hud();
}
function applyTeam(team) {
  for (let i = 0; i < team.length && i < N; i++) {
    const o = i * 2;
    if (team[i] !== homeTeam(i)) { if (cvs[o] === 0) cvs[o] = 1; } // missed the effect: switch at once
    else if (cvs[o] !== 0 || base[i]) { cvs[o] = 0; cvs[o + 1] = 0; base[i] = 0; } // died or slot reused
  }
}
const groupAt = i => { if (groups) for (let g = 0; g < groups.length; g++) if (i >= groups[g][1] && i < groups[g][2]) return g; return i < split ? 0 : armies.length - 1; };
const homeTeam = i => (groups ? groups[groupAt(i)][0] : i < split ? 0 : 1); // the side the unit's group was set up for
const armyAt = i => armies[groupAt(i)] || armies[0];
function effects(f) {
  const fx = deps && deps.fx(), debris = deps && deps.debris(), now = performance.now();
  for (let k = 0; k < f.length; k += 6) {
    const kind = f[k], i = f[k + 1] | 0, x = f[k + 2], y = f[k + 3], z = f[k + 4], o = i * 2;
    if (i < 0 || i >= N) continue;
    const S = armyAt(i).stats, r = S.r;
    if (kind === KIND_RISE) {
      const inPlace = f[k + 5] === 2;
      cvs[o + 1] = 1; if (inPlace) { cvs[o] = 1e-4; base[i] = 0.3; }
      act.push({ i, k: 1, t0: now, swap: inPlace });
      if (!fx) continue;
      for (let q = 0; q < 10; q++) { const a = Math.random() * 6.28, sp = 1 + Math.random() * 2.5; debris.spawn(x + Math.cos(a) * r, y + 0.1, z + Math.sin(a) * r, Math.cos(a) * sp, 3 + Math.random() * 3, Math.sin(a) * sp, 6, 0.1 + Math.random() * 0.12, 18, 6, 0, 0.36, 0.27, 0.16, y); }
      for (let q = 0; q < 8; q++) { const a = Math.random() * 6.28, rr = Math.random() * (r + 0.4); fx.spawn(x + Math.cos(a) * rr, y + 0.2, z + Math.sin(a) * rr, Math.cos(a) * 0.4, 0.7 + Math.random() * 0.8, Math.sin(a) * 0.4, 1.3 + Math.random() * 0.7, 0.35 + r * 0.6 + Math.random() * 0.3, 0, 1.5, 2, 0.42, 1.25, 0.32); }
    } else if (kind === KIND_CONVERT) {
      cvs[o] = Math.max(cvs[o], 1e-4); cvs[o + 1] = -1;
      act.push({ i, k: 2, t0: now, swap: true });
      if (!fx) continue;
      for (let q = 0; q < 18; q++) { const a = Math.random() * 6.28; fx.spawn(x + Math.cos(a) * (r + 0.3), y + Math.random() * S.h, z + Math.sin(a) * (r + 0.3), Math.cos(a) * 0.6, 2 + Math.random() * 2.5, Math.sin(a) * 0.6, 0.8 + Math.random() * 0.5, 0.1 + Math.random() * 0.1, 0, 6, 3, 2.4, 1.9, 0.8); }
      for (let q = 0; q < 12; q++) { const a = (q / 12) * 6.28; fx.spawn(x, y + 0.2, z, Math.cos(a) * 3, 0.3, Math.sin(a) * 3, 0.6, 0.3 + r * 0.3, 0, 1, 2, 1.5, 1.25, 0.6); }
    }
  }
  if (fx) { fx.flush(); debris.flush(); }
}

/** renderUnits() hook, once per frame: fade transitions, draw beams and light pillars. */
export function render(curB, pb, alpha) {
  if (!beamMesh) return;
  const now = performance.now();
  for (let k = 0; k < act.length;) {
    const e = act[k], o = e.i * 2, age = (now - e.t0) / 1000;
    if (e.swap && cvs[o] > 0) cvs[o] = Math.min(1, Math.max(cvs[o], age / 0.6));
    cvs[o + 1] = e.k === 1 ? Math.max(base[e.i], 1 - age / 2.5) : -Math.max(0, 1 - age / 1.4);
    if (age > 2.5) { act[k] = act[act.length - 1]; act.pop(); } else k++;
  }
  let nB = 0, nP = 0;
  if (nb && curB && armies) {
    if (++seenN > 4e9) { seenN = 1; seen.fill(0); }
    const fl = 0.85 + 0.15 * Math.sin(now * 0.02);
    for (let k = 0; k < nb && nB < MAXB; k++) {
      const i = beams[k * 3] | 0, j = beams[k * 3 + 1] | 0, f = beams[k * 3 + 2], oi = i * 10, oj = j * 10;
      if (oj + 9 >= curB.length || oi + 9 >= curB.length || !curB[oi + 9] || !curB[oj + 9]) continue;
      const Si = armyAt(i).stats, Sj = armyAt(j).stats;
      const xi = pb[oi] + (curB[oi] - pb[oi]) * alpha, yi = pb[oi + 1] + (curB[oi + 1] - pb[oi + 1]) * alpha + Si.h * 0.8, zi = pb[oi + 2] + (curB[oi + 2] - pb[oi + 2]) * alpha;
      const xj = pb[oj] + (curB[oj] - pb[oj]) * alpha, gj = pb[oj + 1] + (curB[oj + 1] - pb[oj + 1]) * alpha, zj = pb[oj + 2] + (curB[oj + 2] - pb[oj + 2]) * alpha;
      _d.set(xj - xi, gj + Sj.h * 0.55 - yi, zj - zi); const len = _d.length(); if (len < 0.05) continue;
      _d.multiplyScalar(1 / len); _q.setFromUnitVectors(ZA, _d);
      const w = (0.05 + 0.08 * f) * fl;
      _m.compose(_p.set(xi + _d.x * len / 2, yi + _d.y * len / 2, zi + _d.z * len / 2), _q, _s.set(w, w, len));
      beamMesh.setMatrixAt(nB, _m); beamMesh.setColorAt(nB, _c.setRGB(1.3 + f, 1.05 + f * 0.8, 0.45 + f * 0.3)); nB++;
      if (seen[j] === seenN) continue;
      seen[j] = seenN;
      const rad = (Sj.r + 0.35) * (1.25 - 0.35 * f), h = (Sj.h * 1.6 + 1.5) * (0.3 + 0.7 * f);
      _q.identity(); _m.compose(_p.set(xj, gj, zj), _q, _s.set(rad, h, rad));
      pillarMesh.setMatrixAt(nP, _m); pillarMesh.setColorAt(nP, _c.setRGB(0.35 + 0.65 * f, 0.3 + 0.5 * f, 0.12 + 0.2 * f).multiplyScalar(fl)); nP++;
    }
  }
  beamMesh.count = nB; pillarMesh.count = nP;
  if (nB) { beamMesh.instanceMatrix.needsUpdate = true; beamMesh.instanceColor.needsUpdate = true; }
  if (nP) { pillarMesh.instanceMatrix.needsUpdate = true; pillarMesh.instanceColor.needsUpdate = true; }
}
